import { useEffect, useRef, useState } from "react";
import { wsUrl } from "@/lib/api";

/**
 * Live admin WebSocket connection. Receives device.online / device.offline /
 * device.event broadcasts from the backend and exposes the latest snapshot.
 */
export function useRealtime(enabled = true) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [onlineDeviceIds, setOnlineDeviceIds] = useState([]);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;

    function open() {
      if (stopped) return;
      // Guard: don't open a new socket while the previous one is still
      // CONNECTING or OPEN. This avoids the "closed before connection
      // established" console warning on rapid route changes.
      const prev = wsRef.current;
      if (prev && (prev.readyState === WebSocket.CONNECTING || prev.readyState === WebSocket.OPEN)) {
        return;
      }
      const ws = new WebSocket(wsUrl("/realtime"));
      wsRef.current = ws;
      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "hello" && Array.isArray(data.online_devices)) {
            setOnlineDeviceIds(data.online_devices);
          }
          if (data.type === "device.online") {
            setOnlineDeviceIds((prev) =>
              prev.includes(data.device_id) ? prev : [...prev, data.device_id],
            );
          }
          if (data.type === "device.offline") {
            setOnlineDeviceIds((prev) => prev.filter((id) => id !== data.device_id));
          }
          setLastEvent(data);
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        const delay = Math.min(15000, 1000 * Math.pow(2, retryRef.current++));
        setTimeout(open, delay);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    }

    open();
    return () => {
      stopped = true;
      try {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        // If still CONNECTING, defer close until it opens to avoid the
        // "closed before connection established" warning.
        else if (ws && ws.readyState === WebSocket.CONNECTING) {
          ws.addEventListener("open", () => ws.close(), { once: true });
        }
      } catch {
        // ignore
      }
    };
  }, [enabled]);

  return { connected, lastEvent, onlineDeviceIds };
}
